import {isNil} from 'ramda';

import type {BulkOperationResponseBody} from '../types/api/bulkOperations';

/** Deduplicates refreshes and ensures a terminal event gets detail newer than an in-flight request. */
export function createBulkOperationRefresh(args: {
  load: (bulkOperationId: string) => Promise<BulkOperationResponseBody>;
  isActive: (bulkOperationId: string) => boolean;
  accept: (operation: BulkOperationResponseBody) => void;
}) {
  let generation = 0;
  let active = true;
  const inFlight = new Map<string, Promise<void>>();
  const queuedHydration = new Set<string>();

  const refresh = (bulkOperationId: string, hydrate = false): Promise<void> => {
    if (!active || !args.isActive(bulkOperationId)) {
      return Promise.resolve();
    }
    const existing = inFlight.get(bulkOperationId);
    if (!isNil(existing)) {
      if (hydrate) {
        queuedHydration.add(bulkOperationId);
      }
      return existing;
    }
    const requestGeneration = generation;
    const request = args
      .load(bulkOperationId)
      .then((operation) => {
        if (
          generation === requestGeneration &&
          args.isActive(bulkOperationId) &&
          operation.bulkOperationId === bulkOperationId
        ) {
          args.accept(operation);
        }
      })
      .catch(() => undefined)
      .finally(() => {
        if (inFlight.get(bulkOperationId) === request) {
          inFlight.delete(bulkOperationId);
          if (queuedHydration.delete(bulkOperationId)) {
            void refresh(bulkOperationId);
          }
        }
      });
    inFlight.set(bulkOperationId, request);
    return request;
  };

  return {
    refresh,
    start: () => {
      active = true;
    },
    stop: () => {
      active = false;
      generation += 1;
      inFlight.clear();
      queuedHydration.clear();
    },
  };
}
